import React, {useState} from 'react';
import {getRegexFromPasswordPrefs} from '@netx/core-auth/utils';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	PasswordField,
	type PasswordFieldProps,
} from '@netx/core-components/PasswordField';
import {splitEventHandler} from '@netx/core-next/utils';
import {useFormInput} from '../hooks';
import type {WithLegacyCommonFormFieldProps} from '../types';

export interface LegacyFormPasswordFieldProps
	extends WithLegacyCommonFormFieldProps<PasswordFieldProps> {
	/** field to confirm password input against */
	confirmAgainst?: string;
}

export const LegacyFormPasswordFieldPrimitive = (
	{
		confirmAgainst = 'password1',
		error,
		form,
		formFieldOptions,
		onChange,
		...props
	}: LegacyFormPasswordFieldProps,
	ref: any,
) => {
	const fieldName = props.name ?? (props.isConfirm ? 'password2' : 'password1');
	const _error = typeof error === 'string' ? error : error?.message;

	const [passwordPattern] = useState(getRegexFromPasswordPrefs());

	useFormInput(fieldName, props.value, form, {
		required: props.required,
		validator: useConstCallback(async (value, values) => {
			if (props.isConfirm) {
				if (values?.[confirmAgainst] === value) return true;
				return new Error(i18n.t('i18n.passwordsDoNotMatch'));
			}
			return value.toString().match(passwordPattern) != null
				? true
				: new Error(i18n.t('i18n.invalidValue'));
		}),
		...formFieldOptions,
	});

	const handleChange = useConstCallback(
		splitEventHandler(onChange, (event, value) => {
			form?.setValue(fieldName, value);
		}),
	);

	return (
		<PasswordField
			ref={ref}
			{...props}
			error={_error}
			name={fieldName}
			onChange={handleChange}
		/>
	);
};

export const LegacyFormPasswordField = React.forwardRef(
	LegacyFormPasswordFieldPrimitive,
);

export default LegacyFormPasswordField;
